import React, { useEffect, useState } from 'react';
import { api } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Badge } from '../ui/badge';
import { Loader2, Save, CreditCard, Tag, Trash2, Plus, Copy } from 'lucide-react';
import { toast } from 'sonner';
import FacturasGlobalTab from './FacturasGlobalTab';
import DesviacionesTab from './DesviacionesTab';

export default function PlanesPagosTab() {
  return (
    <Tabs defaultValue="facturas" className="space-y-4">
      <TabsList className="grid grid-cols-3 sm:grid-cols-6 w-full max-w-4xl">
        <TabsTrigger value="facturas" data-testid="tab-facturas">Facturas</TabsTrigger>
        <TabsTrigger value="desviaciones" data-testid="tab-desviaciones">Desviaciones</TabsTrigger>
        <TabsTrigger value="planes" data-testid="tab-planes-config">Plan y precios</TabsTrigger>
        <TabsTrigger value="emisor" data-testid="tab-emisor">Datos emisor</TabsTrigger>
        <TabsTrigger value="stripe" data-testid="tab-stripe">Stripe</TabsTrigger>
        <TabsTrigger value="promocodes" data-testid="tab-promocodes">Códigos</TabsTrigger>
      </TabsList>
      <TabsContent value="facturas"><FacturasGlobalTab /></TabsContent>
      <TabsContent value="desviaciones"><DesviacionesTab /></TabsContent>
      <TabsContent value="planes"><PlanesConfigCard /></TabsContent>
      <TabsContent value="emisor"><EmisorCard /></TabsContent>
      <TabsContent value="stripe"><StripeStatusCard /></TabsContent>
      <TabsContent value="promocodes"><PromocodesCard /></TabsContent>
    </Tabs>
  );
}

function PlanesConfigCard() {
  const [f, setF] = useState({ precio_mensual_por_usuario: 4, descuento_anual_pct: 15, iva_pct: 21, dias_gracia_post_prueba: 5, moneda: 'EUR' });
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get('/superadmin/planes/config').then(r => setF(r.data)).catch(() => {}); }, []);
  const guardar = async () => {
    setSaving(true);
    try {
      await api.put('/superadmin/planes/config', { ...f,
        precio_mensual_por_usuario: parseFloat(f.precio_mensual_por_usuario),
        descuento_anual_pct: parseFloat(f.descuento_anual_pct),
        iva_pct: parseFloat(f.iva_pct),
        dias_gracia_post_prueba: parseInt(f.dias_gracia_post_prueba),
      });
      toast.success('Configuración guardada');
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
    finally { setSaving(false); }
  };
  return (
    <Card>
      <CardHeader>
        <CardTitle className="font-heading">Plan único con precio por usuario</CardTitle>
        <CardDescription>Precio por empleado activo. Se factura mensual o anual. El descuento anual se aplica sobre el importe de 12 meses.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4 max-w-lg">
        <div><Label>Precio mensual por usuario (€)</Label>
          <Input type="number" step="0.01" value={f.precio_mensual_por_usuario} onChange={e => setF({ ...f, precio_mensual_por_usuario: e.target.value })} data-testid="precio-mensual" /></div>
        <div><Label>Descuento anual (%)</Label>
          <Input type="number" step="1" value={f.descuento_anual_pct} onChange={e => setF({ ...f, descuento_anual_pct: e.target.value })} data-testid="descuento-anual" /></div>
        <div><Label>IVA (%)</Label>
          <Input type="number" step="1" value={f.iva_pct} onChange={e => setF({ ...f, iva_pct: e.target.value })} data-testid="iva-pct" /></div>
        <div><Label>Días de gracia tras caducar la prueba</Label>
          <Input type="number" step="1" value={f.dias_gracia_post_prueba} onChange={e => setF({ ...f, dias_gracia_post_prueba: e.target.value })} data-testid="dias-gracia" />
          <p className="text-xs text-zinc-500 mt-1">La empresa sigue accediendo con banner rojo estos días; después queda bloqueada hasta pagar.</p></div>
        <Button onClick={guardar} disabled={saving} className="bg-zinc-900" data-testid="guardar-plan">
          {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />} Guardar
        </Button>
      </CardContent>
    </Card>
  );
}

function EmisorCard() {
  const [f, setF] = useState({ razon_social: '', cif: '', direccion: '', codigo_postal: '', ciudad: '', provincia: '', pais: 'España', iban: '', banco: '', sepa_acreedor_id: '', factura_prefijo: 'F', factura_contador: 0 });
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get('/superadmin/emisor').then(r => setF(prev => ({ ...prev, ...r.data }))).catch(() => {}); }, []);
  const guardar = async () => {
    setSaving(true);
    try {
      await api.put('/superadmin/emisor', { ...f, factura_contador: parseInt(f.factura_contador) || 0 });
      toast.success('Datos del emisor guardados');
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
    finally { setSaving(false); }
  };
  const fld = (key, label, extra = {}) => (
    <div><Label>{label}</Label>
      <Input value={f[key] || ''} onChange={e => setF({ ...f, [key]: e.target.value })} data-testid={`emisor-${key}`} {...extra} /></div>
  );
  return (
    <Card>
      <CardHeader>
        <CardTitle className="font-heading">Datos de emisor (tu empresa)</CardTitle>
        <CardDescription>Aparecerán en las facturas legales. El IBAN se muestra al cliente que elija transferencia y en el fichero SEPA.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3 max-w-2xl">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {fld('razon_social', 'Razón social')}
          {fld('cif', 'CIF / NIF')}
          {fld('direccion', 'Dirección')}
          {fld('codigo_postal', 'Código postal')}
          {fld('ciudad', 'Ciudad')}
          {fld('provincia', 'Provincia')}
          {fld('iban', 'IBAN de cobros')}
          {fld('banco', 'Banco')}
          {fld('sepa_acreedor_id', 'ID de acreedor SEPA (ESxxZZZxxxx)')}
          {fld('factura_prefijo', 'Prefijo de factura')}
          <div><Label>Contador actual</Label>
            <Input type="number" value={f.factura_contador} onChange={e => setF({ ...f, factura_contador: e.target.value })} data-testid="emisor-factura_contador" />
            <p className="text-xs text-zinc-500 mt-1">Última factura emitida. La siguiente será {(parseInt(f.factura_contador) || 0) + 1}.</p></div>
        </div>
        <Button onClick={guardar} disabled={saving} className="bg-zinc-900" data-testid="guardar-emisor">
          {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />} Guardar
        </Button>
      </CardContent>
    </Card>
  );
}

function StripeStatusCard() {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showKeys, setShowKeys] = useState(false);
  const [f, setF] = useState({ publishable_key: '', secret_key: '', webhook_secret: '', mode: 'test' });
  const cargar = () => {
    api.get('/superadmin/stripe/status').then(r => {
      setS(r.data);
      setF(prev => ({ ...prev, mode: r.data.modo || 'test' }));
    }).catch(() => setS({ conectado: false }));
  };
  useEffect(() => { cargar(); }, []);

  const guardar = async () => {
    setSaving(true);
    try {
      await api.put('/superadmin/stripe/config', f);
      toast.success('Configuración de Stripe guardada');
      setShowKeys(false);
      setF({ publishable_key: '', secret_key: '', webhook_secret: '', mode: f.mode });
      cargar();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al guardar');
    } finally { setSaving(false); }
  };

  if (!s) return <p className="text-sm text-zinc-500">Cargando...</p>;
  const webhookUrl = `${window.location.origin}/api/webhook/stripe`;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="font-heading flex items-center gap-2"><CreditCard className="h-5 w-5" /> Configuración de Stripe</CardTitle>
        <CardDescription>Introduce tus claves manualmente para conectar tu cuenta real de Stripe.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Estado actual */}
        <div className="rounded-lg border border-zinc-200 bg-zinc-50 p-4 space-y-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="text-sm font-semibold text-zinc-900">Estado:</span>
              <Badge variant="outline" className={s.conectado ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-red-50 text-red-700 border-red-200'}>
                {s.conectado ? '✓ Conectado' : '✗ No conectado'}
              </Badge>
              <Badge variant="outline" className={s.modo === 'test' ? 'bg-amber-50 text-amber-700 border-amber-200' : 'bg-emerald-50 text-emerald-700 border-emerald-200'}>
                {s.modo === 'test' ? 'TEST' : 'LIVE'}
              </Badge>
            </div>
          </div>
          {s.conectado && (
            <div className="text-xs text-zinc-600 grid grid-cols-2 gap-1 pt-1">
              <span>Cuenta: <b className="font-mono">{s.cuenta_id}</b></span>
              <span>País: <b>{s.pais}</b></span>
              <span>Pagos habilitados: <b>{s.charges_enabled ? 'Sí' : 'No'}</b></span>
              <span>KYC completado: <b>{s.detalles_completados ? 'Sí' : 'No'}</b></span>
            </div>
          )}
          <div className="text-xs text-zinc-500 grid grid-cols-1 gap-1 pt-1 border-t border-zinc-200">
            <span>Clave pública: <span className="font-mono">{s.publishable_key_masked || '— sin configurar —'}</span></span>
            <span>Clave secreta: <span className="font-mono">{s.secret_key_masked || '— sin configurar —'}</span></span>
            <span>Webhook secret: <span className="font-mono">{s.webhook_secret_masked || '— sin configurar —'}</span></span>
          </div>
          {s.error && <p className="text-xs text-red-700 mt-2 pt-2 border-t border-red-200">⚠ {s.error}</p>}
        </div>

        {/* Botón para desplegar el formulario */}
        {!showKeys && (
          <Button variant="outline" onClick={() => setShowKeys(true)} data-testid="stripe-configurar">
            {s.conectado ? 'Cambiar claves' : 'Configurar Stripe manualmente'}
          </Button>
        )}

        {/* Formulario */}
        {showKeys && (
          <div className="space-y-3 rounded-lg border border-blue-200 bg-blue-50/40 p-4">
            <div>
              <Label className="text-sm font-semibold">Modo</Label>
              <div className="flex gap-2 mt-1">
                {['test', 'live'].map(m => (
                  <button key={m} onClick={() => setF({ ...f, mode: m })} data-testid={`stripe-modo-${m}`}
                    className={`flex-1 px-3 py-2 rounded-lg border-2 text-sm font-semibold ${f.mode === m ? (m === 'live' ? 'border-emerald-600 bg-emerald-50' : 'border-amber-500 bg-amber-50') : 'border-zinc-200 bg-white'}`}>
                    {m === 'test' ? 'TEST (pruebas)' : 'LIVE (producción)'}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <Label>Clave pública (Publishable key)</Label>
              <Input value={f.publishable_key} onChange={e => setF({ ...f, publishable_key: e.target.value })}
                placeholder={f.mode === 'live' ? 'pk_live_...' : 'pk_test_...'} data-testid="stripe-pk" />
            </div>
            <div>
              <Label>Clave secreta (Secret key)</Label>
              <Input type="password" value={f.secret_key} onChange={e => setF({ ...f, secret_key: e.target.value })}
                placeholder={f.mode === 'live' ? 'sk_live_...' : 'sk_test_...'} data-testid="stripe-sk" />
            </div>
            <div>
              <Label>Webhook signing secret</Label>
              <Input type="password" value={f.webhook_secret} onChange={e => setF({ ...f, webhook_secret: e.target.value })}
                placeholder="whsec_..." data-testid="stripe-whsec" />
              <p className="text-xs text-zinc-500 mt-1">Se genera al crear el webhook en Stripe (siguiente sección).</p>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => { setShowKeys(false); setF({ publishable_key: '', secret_key: '', webhook_secret: '', mode: f.mode }); }}>Cancelar</Button>
              <Button onClick={guardar} disabled={saving} className="bg-zinc-900" data-testid="stripe-guardar">
                {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />} Guardar claves
              </Button>
            </div>
          </div>
        )}

        {/* Instrucciones */}
        <div className="rounded-lg border border-zinc-200 bg-white p-4 text-sm space-y-3">
          <p className="font-semibold text-zinc-900">Cómo conseguir tus claves de Stripe</p>
          <ol className="list-decimal list-inside space-y-2 text-zinc-700 text-xs">
            <li>
              Entra en tu <a href="https://dashboard.stripe.com/apikeys" target="_blank" rel="noreferrer" className="text-blue-700 underline">panel de Stripe → Developers → API keys</a>.
              Arriba a la derecha activa <b>&quot;Test mode&quot;</b> para probar o desactívalo para producción.
            </li>
            <li>Copia la <b>Publishable key</b> (empieza por <span className="font-mono">pk_test_</span> o <span className="font-mono">pk_live_</span>).</li>
            <li>Pulsa <b>&quot;Reveal test/live secret key&quot;</b> y copia la <b>Secret key</b> (empieza por <span className="font-mono">sk_test_</span> o <span className="font-mono">sk_live_</span>). <b>No la compartas</b>.</li>
            <li>Pégalas arriba y elige el modo correspondiente.</li>
          </ol>

          <p className="font-semibold text-zinc-900 pt-2 border-t border-zinc-200">Configurar el webhook</p>
          <ol className="list-decimal list-inside space-y-2 text-zinc-700 text-xs">
            <li>
              Ve a <a href="https://dashboard.stripe.com/webhooks" target="_blank" rel="noreferrer" className="text-blue-700 underline">Stripe → Developers → Webhooks → Add endpoint</a>.
            </li>
            <li>
              En <b>Endpoint URL</b> pega esta URL de tu servidor:
              <div className="mt-1 flex gap-2 items-center">
                <code className="font-mono text-[11px] bg-zinc-100 border border-zinc-300 rounded px-2 py-1 flex-1 break-all">{webhookUrl}</code>
                <Button size="sm" variant="outline" onClick={() => { navigator.clipboard.writeText(webhookUrl); toast.success('URL copiada'); }} data-testid="copy-webhook">
                  <Copy className="h-3.5 w-3.5" />
                </Button>
              </div>
            </li>
            <li>
              En <b>Events to send</b> selecciona al menos: <span className="font-mono text-[11px]">checkout.session.completed</span>,
              <span className="font-mono text-[11px]"> invoice.paid</span>, <span className="font-mono text-[11px]">invoice.payment_failed</span>.
            </li>
            <li>Tras crear el webhook, Stripe te muestra el <b>Signing secret</b> (empieza por <span className="font-mono">whsec_</span>). Cópialo y pégalo arriba.</li>
          </ol>

          <p className="text-xs text-zinc-500 pt-2 border-t border-zinc-200">
            💡 <b>Tarjeta de prueba en modo TEST</b>: <span className="font-mono">4242 4242 4242 4242</span> · cualquier fecha futura · cualquier CVC. Otras tarjetas de prueba en <a href="https://stripe.com/docs/testing" target="_blank" rel="noreferrer" className="text-blue-700 underline">docs.stripe.com/testing</a>.
          </p>
        </div>
      </CardContent>
    </Card>
  );
}

function PromocodesCard() {
  const [codes, setCodes] = useState([]);
  const [n, setN] = useState({ codigo: '', descuento_pct: 10, activo: true });
  const cargar = () => { api.get('/superadmin/promocodes').then(r => setCodes(r.data)).catch(() => {}); };
  useEffect(() => { cargar(); }, []);
  const crear = async () => {
    try {
      await api.post('/superadmin/promocodes', { codigo: n.codigo, descuento_pct: parseFloat(n.descuento_pct), activo: n.activo });
      toast.success('Código creado'); setN({ codigo: '', descuento_pct: 10, activo: true }); cargar();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };
  const eliminar = async (id) => {
    if (!window.confirm('¿Eliminar este código?')) return;
    await api.delete(`/superadmin/promocodes/${id}`); toast.success('Eliminado'); cargar();
  };
  return (
    <Card>
      <CardHeader>
        <CardTitle className="font-heading flex items-center gap-2"><Tag className="h-5 w-5" /> Códigos promocionales</CardTitle>
        <CardDescription>Descuento porcentual sobre el importe de la factura, aplicable al elegir el plan.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap gap-2 items-end p-3 rounded-lg border border-zinc-200 bg-zinc-50">
          <div className="min-w-[180px]"><Label>Código</Label><Input value={n.codigo} onChange={e => setN({ ...n, codigo: e.target.value.toUpperCase() })} placeholder="BLACKFRIDAY" data-testid="new-code" /></div>
          <div className="w-24"><Label>Descuento %</Label><Input type="number" value={n.descuento_pct} onChange={e => setN({ ...n, descuento_pct: e.target.value })} data-testid="new-code-pct" /></div>
          <Button onClick={crear} className="bg-zinc-900" data-testid="crear-code"><Plus className="h-4 w-4 mr-1" /> Crear</Button>
        </div>
        <table className="w-full text-sm">
          <thead className="text-xs uppercase text-zinc-500"><tr>
            <th className="text-left py-2">Código</th><th className="text-left py-2">Descuento</th><th className="text-left py-2">Usos</th><th></th>
          </tr></thead>
          <tbody className="divide-y divide-zinc-100">
            {codes.map(c => (
              <tr key={c.id} data-testid={`code-row-${c.codigo}`}>
                <td className="py-2 font-mono font-semibold">{c.codigo}</td>
                <td className="py-2">-{c.descuento_pct}%</td>
                <td className="py-2 text-zinc-500">{c.uso_actual || 0} / {c.uso_maximo || '∞'}</td>
                <td className="py-2 text-right"><Button size="icon" variant="ghost" onClick={() => eliminar(c.id)} className="text-red-600 h-7 w-7"><Trash2 className="h-3.5 w-3.5" /></Button></td>
              </tr>
            ))}
            {codes.length === 0 && <tr><td colSpan={4} className="py-6 text-center text-zinc-400">Aún no has creado códigos.</td></tr>}
          </tbody>
        </table>
      </CardContent>
    </Card>
  );
}
